import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useMediasoup from '../hooks/useMediasoup';
import { useExamContext } from '../context/ExamContext';
import { useExamTimer, calculateLiveEndTime, parseExamSchedule } from '../hooks/useExamTimer';
import * as nodeApi from '../api/nodeApi';
import * as phpApi from '../api/phpApi';
import './StaffDashboard.css';
import {
  FaVideo, FaVideoSlash, FaMicrophone, FaMicrophoneSlash,
  FaDesktop, FaUsers, FaPlay, FaPause, FaClock, FaExclamationTriangle,
  FaExpand, FaCircle, FaEye, FaCompress, FaSpinner, FaStop, FaTimes, FaSignOutAlt,
  FaPhone, FaPhoneSlash, FaInfoCircle, FaSync, FaChevronUp, FaChevronDown, FaListAlt
} from 'react-icons/fa';
import ActivityLogModal from './ActivityLogModal';

// --- HELPER COMPONENTS & FUNCTIONS ---

const VideoStream = React.memo(({ stream, autoPlay = true, muted = true, playsInline = true }) => {
  const videoRef = useRef(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const mediaStream = stream?.stream || stream;
    if (!mediaStream) { video.srcObject = null; return; }

    if (video.srcObject !== mediaStream) {
      video.srcObject = mediaStream;
      video.onloadedmetadata = () => { video.play().catch(e => console.warn("Autoplay blocked:", e)); };
    } else if (video.paused) {
      video.play().catch(e => console.warn(e));
    }

    // Handle tab visibility changes to resume hardware decoding/WebRTC streams
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && videoRef.current) {
        // Just resume playback — do NOT reset srcObject (that causes AbortError load-interrupt loop)
        if (videoRef.current.paused) {
          videoRef.current.play().catch(() => { });
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (video && video.srcObject) video.srcObject = null;
    };
  }, [stream]);
  return <video ref={videoRef} autoPlay={autoPlay} muted={muted} playsInline={playsInline} />;
});

const StudentAudio = React.memo(({ stream, muted = true }) => {
  const audioRef = useRef(null);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const mediaStream = stream?.stream || stream;
    if (!mediaStream) { audio.srcObject = null; return; }
    if (audio.srcObject !== mediaStream) {
      audio.srcObject = mediaStream;
    }
  }, [stream]);
  return <audio ref={audioRef} autoPlay playsInline muted={muted} style={{ display: 'none' }} />;
});

const formatViolationType = (type) => {
  if (!type) return '—';
  return String(type).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getStreamLabel = (kind) => {
  if (kind === 'video-1') return 'SYSTEM CAM';
  if (kind === 'video-2') return 'MOBILE CAM';
  if (kind === 'screen') return 'SCREEN';
  return kind.toUpperCase();
};

/**
 * StaffDashboard Component
 */
// Owns the 1 s countdown tick so it re-renders only this badge, not the whole student grid.
const ExamCountdown = React.memo(({ examStarted, examEndTime, serverOffset }) => {
  const { timeRemaining, formatTime } = useExamTimer(examStarted, examEndTime, serverOffset);
  const warn = examEndTime && timeRemaining < 300;
  return (
    <div className="exam-timer" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: warn ? '#fee' : '#f0f9ff',
      borderRadius: '6px',
      fontWeight: '600',
      fontSize: '16px',
      color: warn ? '#c53030' : '#2b6cb0',
      minWidth: '120px',
      justifyContent: 'center',
      border: examEndTime ? '1px solid transparent' : '1px dashed #cbd5e0'
    }}>
      <FaClock />
      <span>
        {examEndTime ? formatTime(timeRemaining) : 'Calculating...'}
      </span>
      {!examEndTime && (
        <FaSpinner className="spinner" style={{ marginLeft: '4px', fontSize: '12px' }} />
      )}
    </div>
  );
});

const NO_STREAMS = [];
const STREAM_KINDS = ['video-1', 'video-2', 'screen'];
const STUDENTS_PER_VIEW = 8; // 2 rows of 4
const STUDENTS_SCROLL_STEP = 4; // one row per arrow click

// Re-renders only when this student's own props change (streams array identity is kept stable by the parent).
const StudentCard = React.memo(({
  student, isCamWatched, isScreenWatched, streams, totalViolations, isPrivateTarget, audioMuted, isExamPaused, handlers,
}) => {
  const isAnyWatched = isCamWatched || isScreenWatched;
  const audioStream = streams.find(s => s.kind === 'audio');
  const id = student.userId;

  return (
    <div
      className={`student-card ${isAnyWatched ? 'is-watched' : ''} ${isExamPaused ? 'is-paused' : ''}`}
      ref={el => handlers.setRef(id, el)}
      data-studentid={id}
    >
      <div className="card-toolbar">
        <div className="card-toolbar-name">
          <div className="student-name" title={`${student.userName}${student.fullName ? ` - ${student.fullName}` : ''}`}>
            {student.userName}{student.fullName ? ` - ${student.fullName.toUpperCase()}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0 }}>
          <button
            type="button"
            className={`student-pause-btn ${isExamPaused ? 'is-paused' : ''}`}
            title={isExamPaused ? 'Resume this student\'s examination' : 'Pause this student\'s examination'}
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={(e) => { e.stopPropagation(); handlers.onPauseStudent(id, isExamPaused); }}
          >
            {isExamPaused ? <FaPlay size={12} /> : <FaPause size={12} />}
          </button>
          <button
            type="button"
            className={`warnings-badge violations-btn ${totalViolations > 0 ? 'has-warnings' : ''}`}
            title="View violation log from records"
            style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={(e) => { e.stopPropagation(); handlers.onViolations(id, student.userName); }}
          >
            <FaExclamationTriangle size={12} />
          </button>
        </div>
      </div>

      <div className="card-body">
        <div className="controls-overlay">
          <button className={`icon-btn ${isCamWatched ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); handlers.onToggleCamera(id); }} title="Toggle Camera Feeds">{isCamWatched ? <FaVideo /> : <FaVideoSlash />}</button>
          <button className={`icon-btn ${isScreenWatched ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); handlers.onToggleScreen(id); }} title="Toggle Screen Share"><FaDesktop /></button>
          <button className="icon-btn" onClick={(e) => { e.stopPropagation(); handlers.onExpand(id); }} title="Expand Grid"><FaExpand /></button>
          <button
            className={`icon-btn ${isPrivateTarget ? 'active' : ''}`}
            onClick={(e) => { e.stopPropagation(); handlers.onPrivateAudio(id); }}
            title="Private Audio Session"
            style={{ color: isPrivateTarget ? '#38a169' : 'inherit' }}
          >
            {isPrivateTarget ? <FaMicrophone /> : <FaMicrophoneSlash />}
          </button>
        </div>

        {isAnyWatched ? (
          <div className="streams-layout quad-view">
            {STREAM_KINDS.map(kind => {
              const s = streams.find(st => st.kind === kind);
              const isReq = kind === 'screen' ? isScreenWatched : isCamWatched;
              return (
                <div key={`${id}-${kind}`} className={`stream-box ${kind}`} style={{ position: 'relative' }}>
                  {isReq && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handlers.onRefresh(id, kind); }}
                      title={`Restart ${getStreamLabel(kind)}`}
                      style={{
                        position: 'absolute', bottom: '5px', right: '5px', zIndex: 10,
                        background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none',
                        borderRadius: '50%', width: '24px', height: '24px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      <FaSync size={10} />
                    </button>
                  )}
                  {isReq && s ? <VideoStream stream={s} autoPlay muted playsInline /> : <div className="placeholder"><small>{isReq ? 'WAIT' : 'OFF'}</small></div>}
                  <div className="stream-label">{getStreamLabel(kind)}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="idle-placeholder" onClick={() => handlers.onToggleCamera(id)}>
            <FaEye size={20} style={{ opacity: 0.3 }} />
            <p>Click to start monitoring</p>
          </div>
        )}

        {/* Invisible audio element for two-way communication - rendered once per card to avoid duplication in expansion */}
        {audioStream && <StudentAudio stream={audioStream} muted={audioMuted} />}
      </div>
    </div>
  );
});

const StaffDashboard = () => {
  const { roomId } = useParams();
  const ctx = useExamContext();
  const [auth, setAuth] = useState(ctx?.decoded ?? null);

  useEffect(() => {
    if (ctx?.decoded) setAuth(ctx.decoded);
  }, [ctx?.decoded]);

  const {
    isConnected, localStreams, remoteStreams, roomUsers, error,
    localVideoRef1, startLocalVideo, startScreenShare, stopScreenShare,
    isScreenSharing, socketRef, requestStudentStream, pauseStudentStreams, refreshStream, isMobileDevice,
    setConsumerPolicy,
  } = useMediasoup(roomId, auth?.user_id, 'staff', auth?.user_name, null, auth?.full_name, auth?.assessment_id);

  // 2. DEFINE MEMOIZED DATA (Uses 'roomUsers' from above)
  const studentRegistryMap = useMemo(() => {
    const map = {};
    (roomUsers || []).forEach(u => {
      if (u.userType === 'student') {
        map[u.userId] = u.registrationId;
      }
    });
    return map;
  }, [roomUsers]);


  // ---------------- LOCAL STATE ----------------
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [students, setStudents] = useState([]);


  const [watchedCameras, setWatchedCameras] = useState(new Set());
  const [watchedScreens, setWatchedScreens] = useState(new Set());
  const [expandedStudentId, setExpandedStudentId] = useState(null);

  // Violations & Modals
  const [violationModalStudentId, setViolationModalStudentId] = useState(null);
  const [violationModalStudentName, setViolationModalStudentName] = useState('');
  const [violationModalData, setViolationModalData] = useState(null);
  const [violationModalLoading, setViolationModalLoading] = useState(false);

  const [studentDbViolationCount, setStudentDbViolationCount] = useState({});

  // Exam Logic
  const [studentBehavior, setStudentBehavior] = useState({});
  const [examStarted, setExamStarted] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const [examEndTime, setExamEndTime] = useState(null);
  const [privateAudioTargetUserId, setPrivateAudioTargetUserId] = useState(null);

  const [serverOffset, setServerOffset] = useState(0);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const closeActivityLog = useCallback(() => setShowActivityLog(false), []);

  // Track which students have been individually started
  const [studentExamStarted, setStudentExamStarted] = useState({});
  // Per-student exam pause (independent of room-wide Stop Examination)
  const [pausedStudentIds, setPausedStudentIds] = useState(() => new Set());
  const [pausingStudentIds, setPausingStudentIds] = useState(() => new Set());

  // Exam start time from token (exam_date + exam_time): enable "Start Examination" only when current time >= scheduled time
  const examStartTimestamp = useMemo(() => {
    const date = auth?.exam_date;
    const time = auth?.exam_time;
    if (!date || !time) return null;
    const schedule = parseExamSchedule(date, time, auth?.duration);
    return schedule?.startMs || null;
  }, [auth?.exam_date, auth?.exam_time, auth?.duration]);
  const [nowForStartCheck, setNowForStartCheck] = useState(() => Date.now());
  useEffect(() => {
    if (examStartTimestamp == null || examStartTimestamp <= Date.now()) return;
    const interval = setInterval(() => {
      const now = Date.now();
      setNowForStartCheck(now);
      if (now >= examStartTimestamp) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [examStartTimestamp]);
  const canStartByTime = examStartTimestamp !== null && nowForStartCheck >= examStartTimestamp;

  const remoteVideoRefs = useRef({});
  const autoStartTriggeredRef = useRef(false);
  const [gotExamStatusFromServer, setGotExamStatusFromServer] = useState(false);

  // ---------------- INITIALIZATION ----------------

  // useEffect(() => {//need to check
  //   const token = searchParams.get('token');
  //   if (!token) return;
  //   try {
  //     const decoded = JSON.parse(atob(token));
  //     setAuth(decoded);
  //   } catch (err) {
  //     console.error('Invalid token structure:', err);
  //   }
  // }, [searchParams]);



  const localStream = localStreams?.[0] || null;
  const localVideoRef = localVideoRef1;

  // ---------------- EFFECTS ----------------

  useEffect(() => {
    if (videoEnabled && localStream && localVideoRef.current) {
      if (localVideoRef.current.srcObject !== localStream) localVideoRef.current.srcObject = localStream;
    }
  }, [videoEnabled, localStream]);

  useEffect(() => {
    const staffStudents = roomUsers.filter((u) => u.userType === 'student');
    const uniqueStudents = Array.from(new Map(staffStudents.map((s) => [s.userId, s])).values());
    setStudents(uniqueStudents);
    // Keep pause UI in sync with server presence (reconnect / multi-proctor).
    setPausedStudentIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      uniqueStudents.forEach((s) => {
        const id = String(s.userId);
        if (s.isExamPaused && !next.has(id)) { next.add(id); changed = true; }
        // Only clear from presence when server explicitly reports not paused (ignore missing flag on partial updates).
        if (s.isExamPaused === false && next.has(id)) { next.delete(id); changed = true; }
      });
      return changed ? next : prev;
    });
  }, [roomUsers]);

  // ---------------- STUDENT WINDOW (8 cards on screen, moved 4 at a time with the arrows) ----------------
  // Only the cards in the window are rendered and only their media is consumed, so hidden students cost no bandwidth.
  const studentRefs = useRef({});
  const [windowStart, setWindowStart] = useState(0);
  const maxWindowStart = Math.max(0, Math.ceil((students.length - STUDENTS_PER_VIEW) / STUDENTS_SCROLL_STEP) * STUDENTS_SCROLL_STEP);
  useEffect(() => {
    if (windowStart > maxWindowStart) setWindowStart(maxWindowStart);
  }, [windowStart, maxWindowStart]);
  const visibleStudents = useMemo(
    () => students.slice(windowStart, windowStart + STUDENTS_PER_VIEW),
    [students, windowStart]
  );
  const visibleIdsKey = visibleStudents.map((s) => String(s.userId)).join(',');
  // Ids as strings, matching the former data-studentid based tracking.
  const visibleStudentIds = useMemo(() => new Set(visibleIdsKey ? visibleIdsKey.split(',') : []), [visibleIdsKey]);
  const scrollStudents = useCallback((direction) => {
    setWindowStart((prev) => Math.min(maxWindowStart, Math.max(0, prev + direction * STUDENTS_SCROLL_STEP)));
  }, [maxWindowStart]);

  // Per-student stream arrays for visible cards. A student's array keeps its identity while its streams are
  // unchanged, so a stream event for one student does not re-render every StudentCard.
  const streamsCacheRef = useRef(new Map());
  const visibleStreamsByUser = useMemo(() => {
    const grouped = new Map();
    remoteStreams.forEach((s) => {
      if (!visibleStudentIds.has(s.userId) && !visibleStudentIds.has(String(s.userId))) return;
      if (!grouped.has(s.userId)) grouped.set(s.userId, []);
      grouped.get(s.userId).push(s);
    });
    const prev = streamsCacheRef.current;
    grouped.forEach((arr, uid) => {
      const old = prev.get(uid);
      if (old && old.length === arr.length && old.every((s, i) => s === arr[i])) grouped.set(uid, old);
    });
    streamsCacheRef.current = grouped;
    return grouped;
  }, [remoteStreams, visibleStudentIds]);

  // Stable handler object for StudentCard; always calls the latest closures.
  const cardHandlersRef = useRef({});
  const cardHandlers = useMemo(() => ({
    setRef: (id, el) => { studentRefs.current[id] = el; },
    onViolations: (id, name) => cardHandlersRef.current.fetchStudentViolations(id, name),
    onPauseStudent: (id, currentlyPaused) => cardHandlersRef.current.toggleStudentPause(id, currentlyPaused),
    onToggleCamera: (id) => cardHandlersRef.current.toggleCameraView(id),
    onToggleScreen: (id) => cardHandlersRef.current.toggleScreenView(id),
    onExpand: (id) => cardHandlersRef.current.toggleExpand(id),
    onPrivateAudio: (id) => cardHandlersRef.current.togglePrivateAudio(id),
    onRefresh: (id, kind) => cardHandlersRef.current.refreshStream(id, kind),
  }), []);

  const prevVisibleStudentIdsRef = useRef(new Set());

  // 4. Auto-connect / auto-disconnect logic
  useEffect(() => {
    const newlyVisible = new Set();
    const newlyHidden = new Set();

    visibleStudentIds.forEach(id => {
      if (!prevVisibleStudentIdsRef.current.has(id)) newlyVisible.add(id);
    });

    prevVisibleStudentIdsRef.current.forEach(id => {
      if (!visibleStudentIds.has(id)) newlyHidden.add(id);
    });

    // Update the ref for next render
    prevVisibleStudentIdsRef.current = new Set(visibleStudentIds);

    // A. Sync toggles visual state
    if (newlyVisible.size > 0 || newlyHidden.size > 0) {
      setWatchedCameras(prev => {
        const next = new Set(prev);
        let changed = false;
        newlyVisible.forEach(id => { if (!next.has(id)) { next.add(id); changed = true; } });
        newlyHidden.forEach(id => { if (next.has(id)) { next.delete(id); changed = true; } });
        return changed ? next : prev;
      });

      setWatchedScreens(prev => {
        const next = new Set(prev);
        let changed = false;
        newlyVisible.forEach(id => { if (!next.has(id)) { next.add(id); changed = true; } });
        newlyHidden.forEach(id => { if (next.has(id)) { next.delete(id); changed = true; } });
        return changed ? next : prev;
      });
    }

    if (!socketRef.current || !isConnected) return;

    // B. Re-subscribe to students who just scrolled in
    newlyVisible.forEach(id => {
      setTimeout(() => {
        requestStudentStream(id);
      }, 50);
    });

    // C. Unsubscribe from students who scrolled out of view
    newlyHidden.forEach(id => {
      pauseStudentStreams(id);
    });

  }, [visibleStudentIds, isConnected, pauseStudentStreams, requestStudentStream, socketRef]);

  // 5. Only receive student media that is actually shown: on-screen (or expanded) cards, with that feed toggled on.
  //    Audio only when it would be audible (same rule as the StudentAudio `muted` prop), and only the laptop mic:
  //    the card plays one audio stream per student, so a dual-device student's phone mic is never consumed.
  useEffect(() => {
    // Ids arrive as strings (data-studentid) or as server values; match either form.
    const has = (set, id) => set.has(id) || set.has(String(id)) || set.has(Number(id));
    setConsumerPolicy((d) => {
      if (d.remoteUserType !== 'student') return true;
      const id = d.remoteUserId;
      const shown = has(visibleStudentIds, id) || (expandedStudentId != null && String(expandedStudentId) === String(id));
      if (!shown) return false;
      if (d.kind === 'audio') {
        if (d.isMobile) return false;
        return privateAudioTargetUserId ? String(privateAudioTargetUserId) === String(id) : audioEnabled;
      }
      if (d.kind === 'screen') return has(watchedScreens, id);
      return has(watchedCameras, id);
    });
  }, [setConsumerPolicy, visibleStudentIds, expandedStudentId, watchedCameras, watchedScreens, audioEnabled, privateAudioTargetUserId]);

  // ---------------- API FUNCTIONS ----------------

  // 🟢 2. UPDATED FETCH VIOLATIONS
  const fetchStudentViolations = useCallback(async (studentId, studentName, openModal = true) => {
    if (!auth) return;

    // Get correct Registration ID from map
    const regId = studentRegistryMap[studentId] || 0;

    if (openModal) {
      setViolationModalStudentId(studentId);
      setViolationModalStudentName(studentName || 'Student');
      setViolationModalData(null);
      setViolationModalLoading(true);
    }

    try {
      const json = await nodeApi.violationList({ user_id: studentId }).catch(() => ({}));
      if (json && json.status === 'success' && json.data) {
        if (openModal) setViolationModalData(json.data);
        setStudentDbViolationCount(prev => ({ ...prev, [studentId]: json.data.total ?? 0 }));
      } else {
        if (openModal) setViolationModalData({ violations: [], total: 0 });
      }
    } catch (err) {
      console.error('Fetch violations error:', err);
      if (openModal) setViolationModalData({ violations: [], total: 0 });
    } finally {
      if (openModal) setViolationModalLoading(false);
    }
  }, [auth, studentRegistryMap]);

  // Timer of a running exam from the room state (duration is changed live by the admin via the PHP portal).
  const applyLiveExamStatus = useCallback((res) => {
    if (!res?.examStarted) return;
    if (res.serverNow) setServerOffset(res.serverNow - Date.now());
    const durationMinutes = parseInt(res.duration, 10);
    if (isNaN(durationMinutes) || durationMinutes <= 0) return;
    const endTs = calculateLiveEndTime(durationMinutes, res.examStartedAt || res.serverNow, res.examDate, res.examTime);
    if (endTs) setExamEndTime(endTs);
  }, []);

  // Safety net for a missed EXAM_TIME_UPDATED push (socket reconnecting, laptop asleep): re-read the room timer.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isConnected || !examStarted) return;
    const sync = () => socket.emit('get-exam-status', { roomId }, applyLiveExamStatus);
    const interval = setInterval(sync, 30000);
    return () => clearInterval(interval);
  }, [socketRef, isConnected, examStarted, roomId, applyLiveExamStatus]);

  // ---------------- SOCKET LISTENERS ----------------
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isConnected) return;

    const handleExamStarted = (res) => {
      console.log('Staff received exam-started event:', res);
      setExamStarted(true);
      setIsStarting(false);
      if (res.serverNow) setServerOffset(res.serverNow - Date.now());
      // 🔹 FIX: Use examStartedAt from socket event (actual start time), fallback to serverNow
      const startTs = res.examStartedAt || res.serverNow || Date.now();
      if (res.duration !== undefined && res.duration !== null) {
        // Ensure duration is a number and in minutes
        const durationMinutes = parseInt(res.duration, 10);
        if (isNaN(durationMinutes) || durationMinutes <= 0) {
          console.error('❌ Invalid duration received:', res.duration);
          return;
        }
        const endTs = calculateLiveEndTime(durationMinutes, startTs, res.examDate, res.examTime);
        if (endTs) {
          setExamEndTime(endTs);
          console.log(`⏰ Staff timer set: startTime=${new Date(startTs).toISOString()}, duration=${durationMinutes}min, endTime=${new Date(endTs).toISOString()}`);
        } else {
          console.error('❌ Failed to calculate examEndTime:', { duration: durationMinutes, startTs });
        }
      } else {
        console.error('❌ exam-started event missing duration:', res);
      }
    };

    socket.on('exam-started', handleExamStarted);

    socket.emit('get-exam-status', { roomId }, (res) => {
      console.log('Staff get-exam-status response:', res);
      if (res?.examStarted) {
        setExamStarted(true);
        if (res.serverNow) setServerOffset(res.serverNow - Date.now());
        const originalStartTime = res.examStartedAt || res.serverNow;
        if (res.duration !== undefined && res.duration !== null) {
          // Ensure duration is a number and in minutes
          const durationMinutes = parseInt(res.duration, 10);
          if (isNaN(durationMinutes) || durationMinutes <= 0) {
            console.error('❌ Invalid duration from get-exam-status:', res.duration);
            return;
          }
          const endTs = calculateLiveEndTime(durationMinutes, originalStartTime, res.examDate, res.examTime);
          if (endTs) {
            setExamEndTime(endTs);
            console.log(`⏰ Staff timer set (get-exam-status): duration=${durationMinutes}min, endTime=${new Date(endTs).toISOString()}`);
          } else {
            console.error('❌ Failed to calculate examEndTime from get-exam-status:', { duration: durationMinutes, originalStartTime });
          }
        } else {
          console.error('❌ get-exam-status response missing duration:', res);
        }
      }

      // 🔹 SYNC PRIVATE CALL STATE
      if (res?.privateAudioTargetUserId !== undefined) {
        setPrivateAudioTargetUserId(res.privateAudioTargetUserId);
        console.log(`📡 Synced private audio target: ${res.privateAudioTargetUserId}`);
      }
      setGotExamStatusFromServer(true);
    });

    const handleViolationUpdate = ({ userId }) => {
      setStudentDbViolationCount(prev => ({
        ...prev,
        [userId]: (prev[userId] || 0) + 1
      }));
    };

    const handleTabChange = (data) => {
      console.log('Staff received student-tab-change:', data);
      handleViolationUpdate(data);
    };
    const handleMonitoringWarning = (data) => {
      console.log('Staff received student-monitoring-warning:', data);
      handleViolationUpdate(data);
    };
    const handleBehaviorScore = ({ userId, score }) => {
      setStudentBehavior(prev => ({
        ...prev,
        [userId]: { ...prev[userId], behaviorScore: score }
      }));
    };
    const handleExamEnded = () => {
      setExamStarted(false);
      setExamEndTime(null);
      if (roomId && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(`examEndedByProctor_${roomId}`, '1');
      }
    };
    const handleStudentExamPauseState = ({ studentUserId, paused } = {}) => {
      if (!studentUserId) return;
      const id = String(studentUserId);
      setPausedStudentIds((prev) => {
        const next = new Set(prev);
        if (paused) next.add(id);
        else next.delete(id);
        return next;
      });
    };

    socket.on('student-tab-change', handleTabChange);
    socket.on('student-monitoring-warning', handleMonitoringWarning);
    socket.on('student-behavior-score', handleBehaviorScore);
    socket.on('exam-ended', handleExamEnded);
    socket.on('student-exam-pause-state', handleStudentExamPauseState);

    const handleExamTimeUpdate = (data = {}) => {
      const myAssessmentId = auth?.assessment_id || auth?.assessmentId;
      if (data.assessment_id && myAssessmentId && String(data.assessment_id) !== String(myAssessmentId)) return;
      console.log('⏰ Staff received EXAM_TIME_UPDATED:', data);

      // Optimistically update countdown immediately if new duration is supplied
      if (data.new_duration) {
        const durationMinutes = parseInt(data.new_duration, 10);
        if (!isNaN(durationMinutes) && durationMinutes > 0) {
          const endTs = calculateLiveEndTime(durationMinutes, null, data.new_exam_date || data.exam_date, data.new_exam_time || data.exam_time);
          if (endTs) setExamEndTime(endTs);
        }
      }

      // Refresh exam status (timer etc) from server to get updated duration/end-time
      socket.emit('get-exam-status', { roomId }, (res) => {
        console.log('Staff get-exam-status (re-sync) response:', res);
        applyLiveExamStatus(res);
      });
      showToast("Exam time has been updated by administrator.", "info");
    };

    socket.on('EXAM_TIME_UPDATED', handleExamTimeUpdate);

    // Another proctor (or the server, when the proctor who started it leaves) changed the private call.
    const handlePrivateAudioState = ({ targetUserId, enabled } = {}) => {
      setPrivateAudioTargetUserId(enabled ? targetUserId : null);
    };
    socket.on('proctor-private-audio-state', handlePrivateAudioState);

    return () => {
      socket.off('proctor-private-audio-state', handlePrivateAudioState);
      socket.off('exam-started', handleExamStarted);
      socket.off('student-tab-change', handleTabChange);
      socket.off('student-monitoring-warning', handleMonitoringWarning);
      socket.off('student-behavior-score', handleBehaviorScore);
      socket.off('exam-ended', handleExamEnded);
      socket.off('student-exam-pause-state', handleStudentExamPauseState);
      socket.off('EXAM_TIME_UPDATED', handleExamTimeUpdate);
    };
  }, [socketRef, isConnected, roomId, auth, showToast, applyLiveExamStatus]);

  // Periodic Sync (Every 30 seconds)
  useEffect(() => {
    if (!students.length) return;

    const fetchAllCounts = async () => {
      try {
        const userIds = students.map(s => s.userId);
        const json = await nodeApi.violationCounts({ userIds });
        if (json?.status === 'success' && json?.data) {
          setStudentDbViolationCount(prev => ({ ...prev, ...json.data }));
        }
      } catch (e) {
        console.error('Failed to fetch violation counts:', e);
      }
    };

    fetchAllCounts();
    const interval = setInterval(fetchAllCounts, 30000);
    return () => clearInterval(interval);
  }, [students.length]);

  // ---------------- HANDLERS ----------------
  const emitProctorMediaState = (video, audio) => {
    if (socketRef.current) {
      socketRef.current.emit('proctor-media-state', {
        videoEnabled: video !== undefined ? video : videoEnabled,
        audioEnabled: audio !== undefined ? audio : audioEnabled
      });
    }
  };

  const toggleVideo = async () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        const newState = !videoEnabled;
        videoTrack.enabled = newState;
        setVideoEnabled(newState);
        emitProctorMediaState(newState, audioEnabled);
      }
    } else {
      try {
        const streams = await startLocalVideo(1);
        // The camera stream also carries the mic; keep it silent until the mic button is turned on.
        const aTrack = streams?.[0]?.getAudioTracks?.()[0];
        if (aTrack) aTrack.enabled = audioEnabled;
        setVideoEnabled(true);
        emitProctorMediaState(true, audioEnabled);
      } catch (e) { console.error(e); }
    }
  };

  const toggleAudio = async () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        const newState = !audioEnabled;
        audioTrack.enabled = newState;
        setAudioEnabled(newState);
        emitProctorMediaState(videoEnabled, newState);
      }
    } else {
      try {
        const streams = await startLocalVideo(1);
        const stream = streams?.[0];
        if (stream) {
          const audioTrack = stream.getAudioTracks()[0];
          if (audioTrack) {
            audioTrack.enabled = true;
            setAudioEnabled(true);
            emitProctorMediaState(videoEnabled, true);
          }
          const vTrack = stream.getVideoTracks()[0];
          if (vTrack && !videoEnabled) vTrack.enabled = false;
        }
      } catch (err) {
        setMediaError(true);
      }
    }
  };

  const togglePrivateAudio = (studentUserId) => {
    if (!socketRef.current || !isConnected) return;

    const isEnding = privateAudioTargetUserId === studentUserId;
    const nextTarget = isEnding ? null : studentUserId;

    setPrivateAudioTargetUserId(nextTarget);

    // 1. If ending a private call and global mic is ON, turn it OFF for privacy (user request).
    if (isEnding && audioEnabled) {
      toggleAudio();
    }

    // Emit the private audio state first so students can mute their local tracks
    socketRef.current.emit('proctor-private-audio-state', {
      targetUserId: nextTarget,
      enabled: !isEnding
    });

    // 2. If starting a private call and global mic is OFF, turn it ON so proctor is audible to target student.
    // Wait a brief moment to ensure TCP signal reaches students before UDP packets flow.
    if (!isEnding && !audioEnabled) {
      setTimeout(() => {
        toggleAudio();
      }, 400); // 400ms delay to allow socket event to propagate
    }
  };

  const handleScreenShareToggle = async () => {
    if (isScreenSharing) {
      await stopScreenShare();
    } else {
      if (isMobileDevice) {
        alert("Screen sharing is not available on mobile devices.");
        return;
      }
      try {
        await startScreenShare();
      } catch (err) {
        alert("Failed to start screen sharing.");
      }
    }
  };

  const handleStartExam = async () => {
    if (!socketRef.current) return;
    setIsStarting(true);

    let examDuration = auth?.duration; // Fallback to token duration

    try {
      const result = await phpApi.startLiveSession({
        room_id: roomId,
        course_code: auth?.course,
        exam_date: auth?.exam_date,
      }).catch(() => ({}));
      if (result.status === 'success' && result.duration !== undefined && result.duration !== null) {
        examDuration = parseInt(result.duration, 10);
        if (isNaN(examDuration) || examDuration <= 0) {
          console.error('❌ Invalid duration from API:', result.duration);
          examDuration = auth?.duration || 180; // Fallback
        }
        console.log('Duration fetched from DB:', examDuration, 'minutes');
      } else {
        console.warn('Duration not in response, using token duration:', examDuration);
      }
    } catch (err) {
      console.error("PHP Session Start Error:", err);
      console.warn('Using token duration as fallback:', examDuration);
    }

    // Ensure duration is a valid number
    examDuration = parseInt(examDuration, 10);
    if (isNaN(examDuration) || examDuration <= 0) {
      console.error('❌ Invalid exam duration, using default 180 minutes');
      examDuration = 180;
    }

    console.log('🚀 Starting exam with duration:', examDuration, 'minutes');
    const payload = {
      roomId,
      duration: examDuration,
      examDate: auth?.exam_date,
      examTime: auth?.exam_time
    };

    socketRef.current.emit('start-exam', payload, (res) => {
      console.log('📡 start-exam callback response:', res);
      setIsStarting(false);
      if (res && res.success) {
        setExamStarted(true);
        if (roomId && typeof sessionStorage !== 'undefined') sessionStorage.removeItem(`examEndedByProctor_${roomId}`);
        // 🔹 FIX: Don't set examEndTime here - wait for 'exam-started' socket event
        console.log('⏰ Exam start initiated, waiting for exam-started event for timer...');
        phpApi.updateLiveSessionStatus({ room_id: roomId, status: 'STARTED' }).catch(err => console.error("Failed to update status to STARTED:", err));
      } else {
        alert("Could not start exam socket.");
      }
    });
  };

  // Auto-start exam when: instructor is first on proctor screen, exam time has passed, and exam was not previously stopped by this proctor (refresh after stop = no auto-start)
  useEffect(() => {
    if (!roomId || !gotExamStatusFromServer || !canStartByTime || examStarted) return;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(`examEndedByProctor_${roomId}`)) return;
    if (autoStartTriggeredRef.current) return;
    autoStartTriggeredRef.current = true;
    handleStartExam();
  }, [gotExamStatusFromServer, canStartByTime, examStarted, roomId, handleStartExam]);

  // Per-student manual start: emits start-student-exam only to that student
  const handleStartStudentExam = useCallback(async (studentId) => {
    if (!socketRef.current) return;
    setStudentExamStarted(prev => ({ ...prev, [studentId]: 'starting' }));

    let examDuration = auth?.duration;
    try {
      const result = await phpApi.startLiveSession({
        room_id: roomId,
        course_code: auth?.course,
        exam_date: auth?.exam_date,
      }).catch(() => ({}));
      if (result.status === 'success' && result.duration != null) {
        const d = parseInt(result.duration, 10);
        if (!isNaN(d) && d > 0) examDuration = d;
      }
    } catch (e) {
      console.warn('phpApi.startLiveSession failed, using token duration:', e);
    }

    examDuration = parseInt(examDuration, 10);
    if (isNaN(examDuration) || examDuration <= 0) examDuration = 180;

    socketRef.current.emit(
      'start-student-exam',
      {
        roomId,
        studentUserId: studentId,
        duration: examDuration,
        examDate: auth?.exam_date,
        examTime: auth?.exam_time,
      },
      (res) => {
        if (res?.success) {
          setStudentExamStarted(prev => ({ ...prev, [studentId]: true }));
        } else {
          console.error('start-student-exam failed:', res?.error);
          setStudentExamStarted(prev => ({ ...prev, [studentId]: false }));
          alert(`Could not start exam for student: ${res?.error || 'Unknown error'}`);
        }
      }
    );
  }, [socketRef, roomId, auth]);

  // Per-student pause / resume (does not stop the room-wide exam for others)
  const toggleStudentPause = useCallback((studentId, currentlyPaused) => {
    if (!socketRef.current || !studentId) return;
    const id = String(studentId);
    if (pausingStudentIds.has(id)) return;
    setPausingStudentIds((prev) => new Set(prev).add(id));
    const event = currentlyPaused ? 'resume-student-exam' : 'pause-student-exam';
    socketRef.current.emit(event, { roomId, studentUserId: id }, (res) => {
      setPausingStudentIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (res?.success) {
        setPausedStudentIds((prev) => {
          const next = new Set(prev);
          if (currentlyPaused) next.delete(id);
          else next.add(id);
          return next;
        });
        showToast(
          currentlyPaused
            ? `Resumed examination for student ${id}.`
            : `Paused examination for student ${id}.`,
          'success'
        );
      } else {
        showToast(res?.error || `Failed to ${currentlyPaused ? 'resume' : 'pause'} student exam.`, 'error');
      }
    });
  }, [socketRef, roomId, pausingStudentIds, showToast]);

  const handleEndExam = () => {
    if (!socketRef.current || isEnding) return;
    if (!window.confirm("Are you sure you want to stop the examination for all students in this room?")) {
      return;
    }
    setIsEnding(true);

    let callbackFired = false;
    const endTimeout = setTimeout(() => {
      if (!callbackFired) {
        callbackFired = true;
        setIsEnding(false);
        setExamStarted(false);
        setExamEndTime(null);
        if (roomId && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(`examEndedByProctor_${roomId}`, '1');
        }
        phpApi.updateLiveSessionStatus({ room_id: roomId, status: 'WAITING' })
          .catch(err => console.error("Failed to update status to WAITING:", err));
        showToast('Exam session has been stopped.', 'success');
      }
    }, 4000);

    socketRef.current.emit('end-exam', { roomId }, (res) => {
      if (callbackFired) return;
      callbackFired = true;
      clearTimeout(endTimeout);
      setIsEnding(false);
      if (res?.success) {
        setExamStarted(false);
        setExamEndTime(null);
        if (roomId && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(`examEndedByProctor_${roomId}`, '1');
        }
        phpApi.updateLiveSessionStatus({ room_id: roomId, status: 'WAITING' })
          .catch(err => console.error("Failed to update status to WAITING:", err));
        showToast('Exam session has been stopped.', 'success');
      } else {
        showToast(res?.error || 'Failed to stop exam on server.', 'error');
      }
    });
  };

  // ---------------- UI TOGGLES ----------------
  const toggleCameraView = (id) => {
    setWatchedCameras(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        if (!watchedScreens.has(id)) requestStudentStream(id);
      }
      return next;
    });
  };

  const toggleScreenView = (id) => {
    setWatchedScreens(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        if (!watchedCameras.has(id)) requestStudentStream(id);
      }
      return next;
    });
  };

  const toggleExpand = (studentId) => {
    if (expandedStudentId === studentId) {
      setExpandedStudentId(null);
    } else {
      if (!watchedCameras.has(studentId)) toggleCameraView(studentId);
      if (!watchedScreens.has(studentId)) toggleScreenView(studentId);
      setExpandedStudentId(studentId);
    }
  };

  const closeViolationModal = () => {
    setViolationModalStudentId(null);
    setViolationModalStudentName('');
    setViolationModalData(null);
  };

  // NOTE: renderPagination removed in favor of virtual scrolling

  cardHandlersRef.current = { fetchStudentViolations, toggleStudentPause, toggleCameraView, toggleScreenView, toggleExpand, togglePrivateAudio, refreshStream };

  // ---------------- RENDER ----------------
  return (
    <div className="dashboard-container">

      {toast && (
        <div className={`staff-toast staff-toast-${toast.type}`}>
          <div className="staff-toast-icon">{toast.type === 'info' && <FaInfoCircle />}</div>
          <div className="staff-toast-message">{toast.message}</div>
        </div>
      )}

      {/* VIOLATIONS MODAL */}
      {violationModalStudentId && (
        <div className="violation-modal-backdrop" onClick={closeViolationModal}>
          <div className="violation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="violation-modal-header">
              <h3>Violations — {violationModalStudentName}</h3>
              <button type="button" className="violation-modal-close" onClick={closeViolationModal}><FaTimes /></button>
            </div>
            <div className="violation-modal-body">
              {violationModalLoading ? (
                <div className="violation-modal-loading"><FaSpinner className="spinner" /> Loading from records...</div>
              ) : violationModalData ? (
                <>
                  <p className="violation-modal-total">Total: <strong>{violationModalData.total ?? 0}</strong> violation(s) recorded</p>
                  {(!violationModalData.violations || violationModalData.violations.length === 0) ? (
                    <p className="violation-modal-empty">No violations in log for this student.</p>
                  ) : (
                    <table className="violation-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Code</th>
                          <th>Severity</th>
                          <th>Count</th>
                          <th>Last at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {violationModalData.violations.map((v, i) => (
                          <tr key={i}>
                            <td>{formatViolationType(v.violation_type)}</td>
                            <td>{v.violation_code || '—'}</td>
                            <td data-severity={v.violation_severity?.toLowerCase()}>{v.violation_severity || '—'}</td>
                            <td>{v.count}</td>
                            <td>{v.last_at ? new Date(v.last_at).toLocaleString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* FULLSCREEN OVERLAY */}
      {expandedStudentId && (() => {
        const student = students.find(s => s.userId === expandedStudentId);
        if (!student) return null;

        const isCamWatched = watchedCameras.has(student.userId);
        const isScreenWatched = watchedScreens.has(student.userId);
        const studentStreams = remoteStreams.filter(s => s.userId === student.userId);
        const behavior = studentBehavior[student.userId] || { behaviorScore: 100 };
        // 🟢 ONLY FROM DB
        const totalViolations = studentDbViolationCount[student.userId] || 0;

        return (
          <div className="fullscreen-overlay">
            <div className="fullscreen-header">
              <div className="fullscreen-info">
                <div>
                  <h2 style={{ marginBottom: '2px' }}>{student.userName} - {student.fullName}</h2>
                </div>
                <div className="status-pills">
                  <button
                    type="button"
                    className={`student-pause-btn large ${pausedStudentIds.has(String(student.userId)) ? 'is-paused' : ''}`}
                    title={pausedStudentIds.has(String(student.userId)) ? 'Resume this student\'s examination' : 'Pause this student\'s examination'}
                    onClick={(e) => { e.stopPropagation(); toggleStudentPause(student.userId, pausedStudentIds.has(String(student.userId))); }}
                  >
                    {pausedStudentIds.has(String(student.userId)) ? <FaPlay size={14} /> : <FaPause size={14} />}
                    {pausedStudentIds.has(String(student.userId)) ? ' Resume' : ' Pause'}
                  </button>
                  <button
                    type="button"
                    className={`warnings-badge large violations-btn ${totalViolations > 0 ? 'has-warnings' : ''}`}
                    onClick={(e) => { e.stopPropagation(); fetchStudentViolations(student.userId, student.userName); }}
                  >
                    <FaExclamationTriangle size={14} /> Violations
                  </button>
                  {/* <span className={`behavior-tag large ${behavior.behaviorScore < 70 ? 'danger' : ''}`}>
                    Exam Integrity: {behavior.behaviorScore}%
                  </span> */}
                  <span className="live-badge">MONITORING LIVE</span>
                </div>
              </div>
              <button className="close-fullscreen-btn" onClick={() => setExpandedStudentId(null)}>
                <FaCompress /> Exit Detailed View
              </button>
            </div>

            <div className="fullscreen-content">
              <div className="fullscreen-grid quad-view">
                {['video-1', 'video-2', 'screen'].map(kind => {
                  const streamObj = studentStreams.find(s => s.kind === kind);
                  const isRequested = kind === 'screen' ? isScreenWatched : isCamWatched;
                  return (
                    <div key={`${student.userId}-${kind}`} className={`fullscreen-stream-box ${kind}`}>
                      {isRequested && streamObj ? (
                        <VideoStream stream={streamObj} autoPlay playsInline muted />
                      ) : (
                        <div className="fs-placeholder">
                          {isRequested && streamObj === undefined && kind !== 'video-2'
                            ? <><FaSpinner className="spinner" /><p>Connecting...</p></>
                            : kind === 'video-2'
                              ? <><FaVideoSlash size={40} /><p style={{ fontSize: '12px', opacity: 0.5 }}>Mobile not connected</p></>
                              : <><FaVideoSlash size={40} /><p>{kind} Off</p></>
                          }
                        </div>
                      )}
                      <div className="fs-label">{getStreamLabel(kind)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="fullscreen-controls">
              <button className={`fs-btn ${isCamWatched ? 'active' : ''}`} onClick={() => toggleCameraView(student.userId)}>
                {isCamWatched ? <FaVideo /> : <FaVideoSlash />} {isCamWatched ? ' Both Cameras Active' : ' Enable Dual Cameras'}
              </button>
              <button className={`fs-btn ${isScreenWatched ? 'active' : ''}`} onClick={() => toggleScreenView(student.userId)}>
                <FaDesktop /> {isScreenWatched ? ' Screen View Active' : ' View Screen Share'}
              </button>
              <button
                className={`fs-btn ${privateAudioTargetUserId === student.userId ? 'active' : ''}`}
                onClick={() => togglePrivateAudio(student.userId)}
                style={{ backgroundColor: privateAudioTargetUserId === student.userId ? '#38a169' : 'var(--primary)' }}
              >
                {privateAudioTargetUserId === student.userId ? <FaMicrophone /> : <FaMicrophoneSlash />}
                {privateAudioTargetUserId === student.userId ? "Mic On" : "Mic Off"}
              </button>
            </div>
          </div>
        );
      })()}

      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="proctor-info">
            <div className="proctor-avatar">{auth?.user_name?.charAt(0) || 'P'}</div>
            <div>
              <h4>{auth?.user_name || 'Staff User'}</h4>
              <span className="badge">AUTHORIZED PROCTOR</span>
            </div>
          </div>
        </div>

        <div className="local-preview">
          {videoEnabled ? <video ref={localVideoRef1} autoPlay playsInline muted /> : <div className="camera-off-placeholder"><FaVideoSlash size={30} /><p>Camera Off</p></div>}
          <div className="preview-controls">
            <button onClick={toggleVideo} className={!videoEnabled ? 'btn-off' : ''} title="Toggle Camera">{videoEnabled ? <FaVideo /> : <FaVideoSlash />}</button>
            <button onClick={toggleAudio} className={!audioEnabled ? 'btn-off' : ''} title="Toggle Mic">{audioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}</button>
            <button onClick={handleScreenShareToggle} className={isScreenSharing ? 'btn-active' : ''} title="Share Screen" disabled={isMobileDevice && !isScreenSharing}>{isScreenSharing ? <FaStop style={{ color: 'red' }} /> : <FaDesktop />}</button>
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-label">
            <span><FaUsers /> Students Connected ({students.length})</span>
          </div>
          <ul className="student-list">
            {students.map(s => {
              const isActive = watchedCameras.has(s.userId) || watchedScreens.has(s.userId);
              return (
                <li key={s.userId} className={isActive ? 'active' : ''} onClick={() => toggleExpand(s.userId)}>
                  <FaCircle className="status-dot" /> {s.userName}
                  {studentDbViolationCount[s.userId] > 0 && <span className="sidebar-violation-indicator">!</span>}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Action Panel: Start Examination (enabled after exam date/time) or Stop Examination (when live) */}
        {!examStarted ? (
          <>
            {examStartTimestamp != null && !canStartByTime && (
              <div style={{ padding: '10px', background: 'rgba(45, 55, 72, 0.08)', border: '1px solid #e2e8f0', borderRadius: '8px', textAlign: 'center', marginBottom: '10px', fontSize: '12px', color: '#4a5568' }}>
                Exam starts at {auth?.exam_date} {auth?.exam_time}
              </div>
            )}
            <button
              className="start-btn-sidebar"
              style={{
                width: '100%',
                backgroundColor: canStartByTime ? '#2b6cb0' : '#a0aec0',
                cursor: canStartByTime && !isStarting ? 'pointer' : 'not-allowed',
                opacity: isStarting ? 0.8 : 1
              }}
              onClick={handleStartExam}
              disabled={!canStartByTime || isStarting}
            >
              {isStarting ? <FaSpinner className="spinner" /> : <FaPlay />} {isStarting ? ' Starting...' : ' Start Examination'}
            </button>
          </>
        ) : (
          <div style={{ marginTop: '20px' }}>
            <div style={{ padding: '15px', background: 'rgba(229, 62, 62, 0.1)', border: '1px solid #e53e3e', borderRadius: '8px', textAlign: 'center', marginBottom: '10px' }}>
              <span className="live-indicator">SESSION LIVE</span>
            </div>
            <button
              className="start-btn-sidebar"
              style={{
                backgroundColor: '#c53030',
                width: '100%',
                cursor: isEnding ? 'not-allowed' : 'pointer',
                opacity: isEnding ? 0.8 : 1
              }}
              onClick={handleEndExam}
              disabled={isEnding}
            >
              {isEnding ? <FaSpinner className="spinner" /> : <FaStop />} {isEnding ? ' Stopping...' : ' Stop Examination'}
            </button>
          </div>
        )}
      </aside>

      {/* MAIN CONTENT GRID */}
      <main className="main-content">
        <header className="top-bar">
          <div className="room-title">
            <h2>Room: {roomId}</h2>
            {examStarted && <span className="live-indicator">LIVE MONITORING</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button
              type="button"
              className="activity-log-open-btn"
              onClick={() => setShowActivityLog(true)}
              title="Registered students and their exam activity"
            >
              <FaListAlt /> Activity Log
            </button>
            {examStarted && (
              <ExamCountdown examStarted={examStarted} examEndTime={examEndTime} serverOffset={serverOffset} />
            )}
            <button
              type="button"
              className="exam-logout-btn"
              onClick={() => {
                window.location.href = phpApi.getPhpBaseUrl() || '/';
              }}
              title="Return to Exam Management"
              style={{
                padding: '8px 16px',
                backgroundColor: '#718096',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500'
              }}
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </header>

        {error && <div className="toast-error" style={{ position: 'relative', margin: '20px' }}><FaExclamationTriangle /> {error}</div>}

        {students.length > STUDENTS_PER_VIEW && (
          <div className="student-window-nav">
            <button type="button" onClick={() => scrollStudents(-1)} disabled={windowStart === 0} title="Previous 4 students">
              <FaChevronUp />
            </button>
            <span>
              {windowStart + 1}–{Math.min(students.length, windowStart + STUDENTS_PER_VIEW)}
              <br />of {students.length}
            </span>
            <button type="button" onClick={() => scrollStudents(1)} disabled={windowStart >= maxWindowStart} title="Next 4 students">
              <FaChevronDown />
            </button>
          </div>
        )}

        <div className={`students-grid${students.length > STUDENTS_PER_VIEW ? ' has-window-nav' : ''}`}>
          {visibleStudents.map((student) => (
            <StudentCard
              key={student.userId}
              student={student}
              isCamWatched={watchedCameras.has(student.userId)}
              isScreenWatched={watchedScreens.has(student.userId)}
              // Off-screen cards keep their DOM box but get no streams, so no hidden <video> is attached.
              streams={visibleStreamsByUser.get(student.userId) || NO_STREAMS}
              totalViolations={studentDbViolationCount[student.userId] || 0}
              isPrivateTarget={privateAudioTargetUserId === student.userId}
              audioMuted={privateAudioTargetUserId ? (privateAudioTargetUserId !== student.userId) : !audioEnabled}
              isExamPaused={pausedStudentIds.has(String(student.userId))}
              handlers={cardHandlers}
            />
          ))}
        </div>

      </main>

      {showActivityLog && (
        <ActivityLogModal roomId={roomId} roomUsers={roomUsers} onClose={closeActivityLog} />
      )}

      <style>{`
        .integrity-footer { padding: 4px 8px; background: #fff5f5; display: flex; align-items: center; gap: 5px; font-size: 10px; color: #e53e3e; border-top: 1px solid #fed7d7; }
        .warnings-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: #718096; padding: 2px 6px; border-radius: 4px; white-space: nowrap; flex-shrink: 0; background: #f1f5f9; border: 1px solid transparent; }
        .warnings-badge.has-warnings { 
          color: #e53e3e; 
          background: rgba(229, 62, 62, 0.1); 
          border-color: rgba(229, 62, 62, 0.2);
          font-weight: 700;
          animation: violation-glow 1.5s ease-in-out infinite;
        }
        @keyframes violation-glow {
          0% { box-shadow: 0 0 0px rgba(229, 62, 62, 0); }
          50% { box-shadow: 0 0 8px rgba(229, 62, 62, 0.4); background: rgba(229, 62, 62, 0.2); }
          100% { box-shadow: 0 0 0px rgba(229, 62, 62, 0); }
        }
        .warnings-badge.large { font-size: 13px; padding: 4px 10px; }
        .status-pills { display: flex; gap: 8px; align-items: center; flex-wrap: nowrap; flex-shrink: 0; }
        .card-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: nowrap; }
        .card-toolbar > div { display: flex; gap: 8px; align-items: center; flex-wrap: nowrap; min-width: 0; }
        .stream-box.video-1, .stream-box.video-2 { background: #000; }
        .stream-box.screen { background: #1a202c; }
        .spinner { animation: spin 1s linear infinite; }
        .camera-off-placeholder { width: 100%; height: 100%; background: #2d3748; display: flex; flex-direction: column; justify-content: center; align-items: center; color: #718096; border-radius: 8px; }
        .btn-off { color: #e53e3e !important; background: rgba(229, 62, 62, 0.2); }
        .btn-active { color: #2b6cb0 !important; background: rgba(43, 108, 176, 0.2); }
        .sidebar-violation-indicator { color: red; font-weight: bold; margin-left: auto; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default StaffDashboard;
